import React, { useEffect, useMemo, useState } from 'react';

import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';

import copies from './copies';
import QaScopeEditor from './QaScopeEditor';
import QaScopeTable from './QaScopeTable';
import { buildScopeRows, findScopeConfiguration, QA_EXPERT_KEY } from './scope';
import type { ScopeConfiguration, ScopeRow } from './types';

const QaConfigurationTab: React.FC = () => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const baseUrl = `/sysconfig/api/v1/organizations/${organization.id}/ai_experts`;

  const rows = useMemo(() => buildScopeRows(organization), [organization]);
  const [editScope, setEditScope] = useState<ScopeRow | null>(null);

  // Close any open editor when the sysconfig target org changes: the editor holds the previous org's
  // scope row and its stored configuration, and a save would PUT them against the newly selected org.
  useEffect(() => {
    setEditScope(null);
  }, [organization.id]);

  // One unfiltered read for the whole organization rather than one request per scope: the table
  // needs a status for every row it renders. The read answers every expert's rows, so the QA
  // expert's are selected here.
  const {
    isLoading,
    isError,
    data: scopeConfigurations,
    reloadData,
  } = useDataFetcher<ScopeConfiguration[]>(`${baseUrl}/scopes`);

  const qaConfigurations = useMemo(
    () => (scopeConfigurations ?? []).filter((configuration) => configuration.expert_key === QA_EXPERT_KEY),
    [scopeConfigurations]
  );

  return (
    <div className="flex min-h-[50vh] flex-col gap-16 bg-white p-24">
      <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD}>
        {copies.tabTitle}
      </Text>
      {isLoading || isError ? (
        <SkeletonTable rowsCount={5} columnsCount={3} isError={isError} />
      ) : (
        <QaScopeTable rows={rows} configurations={qaConfigurations} onConfigure={setEditScope} />
      )}
      {editScope ? (
        <QaScopeEditor
          scope={editScope}
          configuration={findScopeConfiguration(qaConfigurations, editScope)}
          baseUrl={baseUrl}
          onClose={() => setEditScope(null)}
          onSaved={reloadData}
        />
      ) : null}
    </div>
  );
};

export default QaConfigurationTab;
